import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useOutletContext,
  useNavigate,
} from "react-router-dom";

import { useBreadStorageContext } from "contexts/BreadStorageContext";
import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import { cloneDeep } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { createColumnHelper } from "@tanstack/react-table";

import {
  Grid,
  Box,
  ClickAwayListener,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";

import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";

import TableField from "components/Table";
import SearchInput from "components/SearchInput";
import { SolidButton } from "components/buttons/SolidButton";
import ModalContainer from "components/ModalContainer";
import GlobalAlert from "components/Notification";

import ModalforCheck from "../modalCheck.js";
import ModalForAddOrgOrSite from "../ModalForAddOrgOrSite.js";
import ModalSetupMsp from "./ModalSetupMsp";

import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";
import nextIcon from "assets/images/icons/icn_pagination_next.svg";

import { mergeRealmData } from "utils/getHierarchy";
import EllipsisTooltipCell from "utils/ellipsisTooltipCell";
import { MSP_DAILY_CREDITS, MSP_MINIMUM_CREDIT_DAYS } from "const/license";
const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const MSP_MINIMUM_CREDITS = MSP_DAILY_CREDITS * MSP_MINIMUM_CREDIT_DAYS;
const getMspLicenseKeySignature = (licenseKeys) => licenseKeys
  .map((item) => item.segments.join("").toUpperCase())
  .join("|");

const SupportBadge = ({ badge }) => {
  const displayName = badge?.currentSupportDisplayName;

  if (!displayName) return null;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        height: "18px",
        ml: "7px",
        px: "7px",
        borderRadius: "9px",
        backgroundColor: "#DDF8DF",
        color: "#008A20",
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: "18px",
        whiteSpace: "nowrap",
        boxSizing: "border-box",
      }}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          width: "13px",
          height: "13px",
          mr: "4px",
          borderRadius: "50%",
          backgroundColor: "#D9EDFF",
        }}
      >
        <ShieldOutlinedIcon
          sx={{
            width: "10px",
            height: "10px",
            color: "#3B9BE7",
          }}
        />
      </Box>

      {`Support · ${displayName}`}
    </Box>
  );
};

const Realm = () => {
  const {
    scopeData,
    setScopeData,
  } = useOutletContext();

  const isSurveillanceSystem =
    localStorage.getItem("selectedSystem") === "surveillance";
  const membershipApiKey = isSurveillanceSystem
    ? "/me/memberships/surveillance.read"
    : "/me/memberships.read";

  const {
    setBread,
  } = useBreadStorageContext();

  const [realmListUuid, setRealmListUuid] = useState(uuidv4());

  const {
    dataMap,
    getData,
    sendRequest,
  } = useDataSourceManager();

  const [openAddNewAction, setOpenAddNewAction] = useState(false);
  const addNewButtonRef = useRef(null);
  const [openMspModal, setOpenMspModal] = useState(false);
  const [mspName, setMspName] = useState("");
  const [mspSetupStep, setMspSetupStep] = useState(1);
  const [mspLicenseKeys, setMspLicenseKeys] = useState([
    { id: uuidv4(), segments: ["", "", "", ""] },
  ]);
  const [mspPreconfirm, setMspPreconfirm] = useState(null);
  const [mspPreconfirmUuid, setMspPreconfirmUuid] = useState("");
  const [mspPreconfirmSignature, setMspPreconfirmSignature] = useState("");
  const [isPreconfirmingMsp, setIsPreconfirmingMsp] = useState(false);
  const [isCreatingMsp, setIsCreatingMsp] = useState(false);
  const [mspCreateUuid] = useState(uuidv4());
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });

  const [
    openModalForAddOrg,
    setOpenModalForAddOrg,
  ] = useState(false);

  const [
    modalForAddOrgTitle,
    setModalForAddOrgTitle,
  ] = useState("");

  const [
    modalForAddOrgCancelBtn,
    setModalForAddOrgCancelBtn,
  ] = useState("");

  const [
    modalForAddOrgSaveBtn,
    setModalForAddOrgSaveBtn,
  ] = useState("");

  const [
    openModalCheck,
    setOpenModalCheck,
  ] = useState(false);

  const [
    modalCheckTitle,
    setModalCheckTitle,
  ] = useState("");

  const [
    modalCheckContent,
    setModalCheckContent,
  ] = useState("");

  const [
    modalCheckCancelBtn,
    setModalCheckCancelBtn,
  ] = useState("");

  const [
    modalCheckSaveBtn,
    setModalCheckSaveBtn,
  ] = useState("");

  const [modalFrom, setModalFrom] = useState("");
  const [modalAction, setModalAction] = useState(false);
  const [updateItem, setUpdateItem] = useState({});

  const [
    supportBadges,
    setSupportBadges,
  ] = useState({});

  const [
    supportBadgeRequestMap,
    setSupportBadgeRequestMap,
  ] = useState({});

  const requestedSupportBadgesRef = useRef(new Set());

  const processedSupportBadgesRef = useRef(new Set());

  const processedRealmListUuidRef = useRef("");

  const getDatas = async () => {
    const requestUuid = uuidv4();

    setRealmListUuid(requestUuid);

    sendRequest(
      membershipApiKey,
      requestUuid
    );
  };

  const getRealmRowKey = useCallback((item) => {
    if (!item) return "";

    const entId =
      item.entId ||
      item.enterpriseId ||
      "";

    const orgId =
      item.orgId ||
      item.organizationId ||
      item.id ||
      "";

    if (item.hasManageOrg === false) {
      return `org-${orgId}`;
    }

    return `ent-${entId || orgId}-${orgId}`;
  }, []);

  const normalizeSupportBadges = useCallback(
    (response) => {
      const items =
        response?.result?.data?.items ||
        response?.data?.items ||
        response?.items ||
        [];

      if (!Array.isArray(items)) {
        return [];
      }

      return items.filter(
        (item) =>
          item?.status === "active" &&
          item?.currentSupportDisplayName
      );
    },
    []
  );

  const loadSupportBadge = useCallback(
    (item) => {
      if (!item) {
        return;
      }

      let supportSession = null;

      try {
        supportSession = JSON.parse(
          sessionStorage.getItem("supportSession") || "null",
        );
      } catch (error) {
        console.error(
          "[SupportBadge] Invalid supportSession:",
          error,
        );
      }

      if (supportSession?.supportTokenId) {
        return;
      }

      const isStandalone =
        item?.hasManageOrg === false;

      if (!isStandalone) {
        return;
      }

      const orgId =
        item?.orgId ??
        item?.organizationId ??
        item?.id ??
        "";

      if (!orgId) {
        console.warn(
          "[SupportBadge] orgId is missing:",
          item,
        );

        return;
      }

      const rowKey = getRealmRowKey(item);

      if (!rowKey) {
        return;
      }

      const requestKey =
        `standalone-${orgId}`;

      if (
        requestedSupportBadgesRef.current.has(
          requestKey,
        )
      ) {
        return;
      }

      requestedSupportBadgesRef.current.add(
        requestKey,
      );

      const requestUuid =
        `support-${uuidv4()}`;

      setSupportBadgeRequestMap(
        (previous) => ({
          ...previous,
          [requestUuid]: rowKey,
        }),
      );

      sendRequest(
        "/orgs/:orgId/supportTokens/badges.read",
        requestUuid,
        {
          orgId,
        },
      );
    },
    [
      getRealmRowKey,
      sendRequest,
    ],
  );


  useEffect(() => {
    if (!Array.isArray(scopeData?.realm)) {
      return;
    }

    scopeData.realm.forEach((item) => {
      loadSupportBadge(item);
    });
  }, [
    scopeData?.realm,
    loadSupportBadge,
  ]);

  useEffect(() => {
    const requestEntries = Object.entries(
      supportBadgeRequestMap
    );

    if (requestEntries.length === 0) {
      return;
    }

    requestEntries.forEach(
      ([requestUuid, rowKey]) => {
        if (
          processedSupportBadgesRef.current.has(
            requestUuid
          )
        ) {
          return;
        }

        const response = dataMap[requestUuid];

        if (response === undefined) {
          return;
        }

        processedSupportBadgesRef.current.add(
          requestUuid
        );

        if (response?.code !== undefined) {
          console.error(
            "Load support badge failed:",
            response
          );

          setSupportBadges((prev) => ({
            ...prev,
            [rowKey]: [],
          }));
        } else {
          const badges =
            normalizeSupportBadges(response);

          setSupportBadges((prev) => ({
            ...prev,
            [rowKey]: badges,
          }));
        }

        setSupportBadgeRequestMap((prev) => {
          const next = {
            ...prev,
          };

          delete next[requestUuid];

          return next;
        });
      }
    );
  }, [
    dataMap,
    supportBadgeRequestMap,
    normalizeSupportBadges,
  ]);

  const handleAddItem = () => {
    setOpenAddNewAction((open) => !open);
  };

  const handleAddStandalone = () => {
    setOpenAddNewAction(false);
    setModalFrom("Add");

    const newItem = {
      id: "",
      name: "",
      hasManageOrg: false,
      roleId: "",
      roleName: "",
      createdAt: "",
      createdTime: "",
    };

    setUpdateItem(newItem);

    setModalForAddOrgTitle("Add Organization");
    setModalForAddOrgCancelBtn("Cancel");
    setModalForAddOrgSaveBtn("Save");
    setOpenModalForAddOrg(true);
  };

  const resetMspSetup = () => {
    setMspName("");
    setMspSetupStep(1);
    setMspLicenseKeys([{ id: uuidv4(), segments: ["", "", "", ""] }]);
    setMspPreconfirm(null);
    setMspPreconfirmUuid("");
    setMspPreconfirmSignature("");
    setIsPreconfirmingMsp(false);
  };

  const handleApplyMspLicenseKeys = () => {
    const licenseKeys = mspLicenseKeys
      .map((item) => item.segments.join("").toUpperCase())
      .filter((key) => key.length === 20);

    if (licenseKeys.length === 0 || isPreconfirmingMsp) {
      return;
    }

    const requestUuid = uuidv4();

    setMspPreconfirmUuid(requestUuid);
    setMspPreconfirmSignature(licenseKeys.join("|"));
    setIsPreconfirmingMsp(true);
    sendRequest("/ents/preconfirm.read", requestUuid, null, { licenseKeys });
  };

  const handleCreateMsp = () => {
    const name = mspName.trim();
    const redeemedCredits = (mspPreconfirm?.licenseKeys || []).reduce(
      (total, item) => total + (item.status === "VALID" ? Number(item.point || 0) : 0),
      0,
    );
    const licenseKeys = mspLicenseKeys
      .filter((item, index) => mspPreconfirm?.licenseKeys?.[index]?.status === "VALID")
      .map((item) => item.segments.join("").toUpperCase());
    if (
      !name
      || !mspPreconfirm?.sufficientCredits
      || mspPreconfirmSignature !== getMspLicenseKeySignature(mspLicenseKeys)
      || redeemedCredits < MSP_MINIMUM_CREDITS
      || !licenseKeys.length
      || isCreatingMsp
    ) {
      return;
    }

    setIsCreatingMsp(true);
    sendRequest("/ents.create", mspCreateUuid, null, {
      data: { entName: name, licenseKeys },
    });
  };

  const handleScopeLink = (item) => {
    const tmpSelectedBread = {
      realm: item,
      orgs: {},
      site: {},
    };

    localStorage.setItem(
      "selectBreadcrumbs",
      JSON.stringify(tmpSelectedBread)
    );

    setBread(tmpSelectedBread);

    navigatePage(item);
  };

  const navigate = useNavigate();

  const navigatePage = (item) => {
    let path = "";

    if (item.hasManageOrg === false) {
      path = "/org/sites";
    } else {
      path = "/msp/organizations";
    }

    navigate(path);
  };

  function handleActionDenied(message) {
    setOpenModalForAddOrg(false);
    setModalCheckTitle("Action denied");
    setModalCheckContent(message);
    setModalCheckCancelBtn("");
    setModalCheckSaveBtn("OK");
    setModalFrom("error");
    setOpenModalCheck(true);
  }

  useEffect(() => {
    const response = dataMap[realmListUuid];

    if (
      response === undefined ||
      processedRealmListUuidRef.current === realmListUuid
    ) {
      return;
    }

    processedRealmListUuidRef.current = realmListUuid;

    if (response?.code !== undefined) {
      setModalCheckTitle("Action denied");
      setModalCheckContent(
        response.message
      );
      setModalCheckCancelBtn("");
      setModalCheckSaveBtn("OK");
      setModalFrom("error");
      setOpenModalCheck(true);
    } else {
      const tmpEntData = {
        data: [],
      };

      const tmpAloneOrgData = {
        data: [],
      };

      if (
        response.data
          .enterprises &&
        response.data
          .enterprises.length > 0
      ) {
        tmpEntData.data =
          response.data.enterprises;
      }

      if (
        response.data
          .standaloneOrgs &&
        response.data
          .standaloneOrgs.length > 0
      ) {
        tmpAloneOrgData.data =
          response.data.standaloneOrgs;
      }

      const result = mergeRealmData(
        tmpEntData,
        tmpAloneOrgData,
        { system: isSurveillanceSystem ? "surveillance" : "network" },
      );

      /**
       * 清除舊的 badge 資料，
       * 讓重新整理後重新取得。
       */
      requestedSupportBadgesRef.current =
        new Set();

      processedSupportBadgesRef.current =
        new Set();

      setSupportBadges({});
      setSupportBadgeRequestMap({});

      setScopeData((prev) => ({
        ...prev,
        realm: result,
      }));
    }
  }, [
    dataMap,
    realmListUuid,
    setScopeData,
  ]);

  useEffect(() => {
    if (!mspPreconfirmUuid) {
      return;
    }

    const response = getData(mspPreconfirmUuid);

    if (!response) {
      return;
    }

    setIsPreconfirmingMsp(false);

    if (response.code) {
      setMspPreconfirm(null);
      setAlertInfo({
        open: true,
        severity: "error",
        message: response.message || "Unable to validate license keys.",
      });
      return;
    }

    const data = response.data || response.result?.data;

    if (!data?.licenseKeys) {
      setMspPreconfirm(null);
      setAlertInfo({
        open: true,
        severity: "error",
        message: "No license key result was returned.",
      });
      return;
    }

    setMspPreconfirm(data);
  }, [dataMap, getData, mspPreconfirmUuid]);

  useEffect(() => {
    const response = getData(mspCreateUuid);

    if (!response) {
      return;
    }

    if (response.code) {
      setIsCreatingMsp(false);
      setModalCheckTitle("Action denied");
      setModalCheckContent(response.message);
      setModalCheckCancelBtn("");
      setModalCheckSaveBtn("OK");
      setModalFrom("error");
      setOpenModalCheck(true);
      return;
    }

    setIsCreatingMsp(false);
    setOpenMspModal(false);
    resetMspSetup();
    getDatas();
  }, [dataMap, getData, mspCreateUuid]);

  useEffect(() => {
    if (modalAction === true) {
      if (modalFrom === "Add") {
        if (checkMockup === "true") {
          const addItem = cloneDeep(updateItem);

          addItem.id = uuidv4();

          setScopeData((prev) => ({
            ...prev,
            realm: [
              ...prev.realm,
              addItem,
            ],
          }));
        }
      }

      setModalAction(false);
    }
  }, [
    modalAction,
    modalFrom,
    updateItem,
    setScopeData,
  ]);

  const columnHelper = createColumnHelper();

  const renderDeviceCounts = (devices) => (
    `AP ${devices?.accessPoints ?? 0} · Switch ${devices?.switches ?? 0}`
  );

  const renderSurveillanceDeviceCounts = (devices) => (
    `${devices?.online ?? 0} | ${devices?.offline ?? 0} | ${devices?.onboarding ?? 0}`
  );

  const renderAlerts = (alerts) => (
    `${alerts?.critical ?? 0} | ${alerts?.normal ?? 0} | ${alerts?.low ?? 0}`
  );

  const renderCountTooltip = (value, label) => (
    <Tooltip title={label} arrow placement="bottom">
      <Box component="span" sx={{ cursor: "default" }}>
        {value}
      </Box>
    </Tooltip>
  );

  const renderAddNewButton = () => (
    <Box>
      <Box ref={addNewButtonRef} sx={{ display: "inline-flex" }}>
        <SolidButton
          startIcon={<AddIcon />}
          height="32px"
          onClick={handleAddItem}
        >
          Add New
        </SolidButton>
      </Box>
      <Popper
        open={openAddNewAction}
        anchorEl={addNewButtonRef.current}
        placement="bottom-end"
        strategy="fixed"
        modifiers={[
          { name: "offset", options: { offset: [0, 8] } },
          { name: "flip", options: { fallbackPlacements: ["top-end"] } },
        ]}
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setOpenAddNewAction(false)}>
          <Box
            sx={{
              position: "relative",
              width: "251px",
              bgcolor: "primary.lightBlue",
              px: "20px",
              py: "10px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
              borderRadius: 4,
              zIndex: 1300,
              "&::before": {
                content: '""',
                position: "absolute",
                top: "-8px",
                right: "20px",
                width: 0,
                height: 0,
                borderLeft: "8px solid transparent",
                borderRight: "8px solid transparent",
                borderBottom: "8px solid",
                borderBottomColor: "primary.lightBlue",
              },
            }}
          >
            <Box
              onClick={() => {
                setOpenAddNewAction(false);
                resetMspSetup();
                setOpenMspModal(true);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "211px",
                height: "50px",
                px: "10px",
                color: "primary.main",
                cursor: "pointer",
                borderBottom: "1px solid",
                borderColor: "primary.borderGreyPrimary",
                "&:hover": { color: "primary.mainHover" },
              }}
            >
              <span>Set Up New MSP</span>
              <img src={nextIcon} alt="" width="20" />
            </Box>
            <Box
              onClick={handleAddStandalone}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "211px",
                height: "50px",
                px: "10px",
                color: "primary.main",
                cursor: "pointer",
                "&:hover": { color: "primary.mainHover" },
              }}
            >
              <span>Add a Standalone</span>
              <img src={nextIcon} alt="" width="20" />
            </Box>
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );

  const columnsData = [
    columnHelper.accessor("name", {
      header: "Name",

      cell: (info) => {
        const rowData = info.row.original;
        const rowKey = getRealmRowKey(rowData);

        const badges =
          supportBadges[rowKey] || [];

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
            }}
          >
            <EllipsisTooltipCell
              text={info.getValue()}
              onClick={() =>
                handleScopeLink(rowData)
              }
              sx={{
                color: "#707ca8",
                cursor: "pointer",
                textDecoration: "underline",
                flex: "1 1 0",
                minWidth: 0,
              }}
            />

            {badges.map((badge) => (
              <SupportBadge
                key={badge.supportTokenId}
                badge={badge}
              />
            ))}
          </Box>
        );
      },
    }),

    columnHelper.accessor("roleName", {
      header: "Role",
    }),

    columnHelper.accessor("type", {
      header: "Type",
    }),

    columnHelper.accessor("organizationCount", {
      header: "Organization",
      cell: (info) => info.getValue() ?? "-",
    }),

    columnHelper.accessor("siteCount", {
      header: "Site",
    }),

    ...(isSurveillanceSystem
      ? [
        columnHelper.accessor("cameras", {
          header: "Camera",
          cell: (info) => renderCountTooltip(
            renderSurveillanceDeviceCounts(info.getValue()),
            "Online | Offline | Onboarding",
          ),
        }),
        columnHelper.accessor("nvrs", {
          header: "NVR",
          cell: (info) => renderCountTooltip(
            renderSurveillanceDeviceCounts(info.getValue()),
            "Online | Offline | Onboarding",
          ),
        }),
      ]
      : [
        columnHelper.accessor("adoptedDevices", {
          header: "Adopted Devices",
          cell: (info) => renderDeviceCounts(info.getValue()),
        }),
        columnHelper.accessor("inventoryDevices", {
          header: "Inventory Devices",
          cell: (info) => renderDeviceCounts(info.getValue()),
        }),
        columnHelper.accessor("clients", {
          header: "Clients",
        }),
        columnHelper.accessor("alerts", {
          header: "Alerts",
          cell: (info) => renderCountTooltip(
            renderAlerts(info.getValue()),
            "Critical | Normal | Low",
          ),
        }),
      ]),

    columnHelper.accessor("createdTime", {
      header: "Created Time",
    }),
  ];

  const realmData = Array.isArray(
    scopeData?.realm
  )
    ? scopeData.realm
    : [];

  const table = useTableInstance({
    tableName: "hierarchyRealmMsp",
    data: realmData,
    columns: columnsData,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });

  const emptyHead = useTableInstance({
    tableName: "emptyRealmMsp",
    data: "",
    columns: columnsData,
    enableSorting: true,
  });

  return (
    <>
      <div
        className="main-card-content"
        style={{
          marginTop: "60px",
          flex:
            realmData.length > 0
              ? ""
              : "1",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div>
          <Grid
            container
            style={{
              justifyContent: "space-between",
              marginBottom: "10px",
            }}
          >
            <Box
              style={{
                display: "flex",
              }}
            >
              <Grid
                container
                sx={{
                  alignItems: "center",
                }}
              >
                <Typography
                  fontSize="16px"
                  fontWeight="600"
                  color="rgba(0, 0, 0, 0.8)"
                >
                  Realm Home
                </Typography>
              </Grid>
            </Box>

            <Box
              style={{
                display: "flex",
              }}
            >
              {realmData.length > 0 && (
                <Grid
                  container
                  sx={{
                    alignItems: "center",
                    gap: "15px",
                  }}
                >
                  <SearchInput
                    value={table.globalFilter}
                    onChange={
                      table.setGlobalFilter
                    }
                  />

                  {renderAddNewButton()}
                </Grid>
              )}
            </Box>
          </Grid>
        </div>

        {realmData.length > 0 && (
          <TableField {...table} />
        )}

        {realmData.length === 0 && (
          <>
            <TableField {...emptyHead} />

            <div
              style={{
                flex: "1",
                alignContent: "center",
                justifyItems: "center",
              }}
            >
              <Grid
                container
                style={{
                  justifyContent: "center",
                  marginBottom: "40px",
                }}
              >
                <Typography
                  sx={{
                    textAlign: "center",
                  }}
                  fontSize="16px"
                  fontWeight="600"
                  color="rgba(0, 0, 0, 0.8)"
                >
                  No entry in the table, you can create a new MSP or Standalone Organization.
                </Typography>
              </Grid>

              {renderAddNewButton()}
            </div>
          </>
        )}
      </div>

      <ModalSetupMsp
        openModal={openMspModal}
        mspName={mspName}
        step={mspSetupStep}
        licenseKeys={mspLicenseKeys}
        preconfirm={mspPreconfirm}
        isPreconfirmCurrent={mspPreconfirmSignature === getMspLicenseKeySignature(mspLicenseKeys)}
        isPreconfirming={isPreconfirmingMsp}
        isCreating={isCreatingMsp}
        onChangeMspName={setMspName}
        onChangeStep={setMspSetupStep}
        onAddLicenseKey={() => {
          setMspLicenseKeys((prev) => [
            ...prev,
            { id: uuidv4(), segments: ["", "", "", ""] },
          ]);
        }}
        onChangeLicenseKey={(id, segmentIndex, value) => {
          setMspLicenseKeys((prev) => prev.map((item) => (
            item.id === id
              ? {
                ...item,
                segments: item.segments.map((segment, index) => (
                  index === segmentIndex ? value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5) : segment
                )),
              }
              : item
          )));
        }}
        onRemoveLicenseKeys={(ids) => {
          setMspLicenseKeys((prev) => prev.filter((item) => !ids.includes(item.id)));
        }}
        onApplyLicenseKeys={handleApplyMspLicenseKeys}
        onCloseModal={() => {
          setOpenMspModal(false);
          resetMspSetup();
        }}
        onCreateMsp={handleCreateMsp}
      />

      <ModalContainer
        display={openModalCheck}
        modalWidth="500px"
        children={
          <ModalforCheck
            title={modalCheckTitle}
            content={modalCheckContent}
            cancelBtn={
              modalCheckCancelBtn
            }
            saveBtn={modalCheckSaveBtn}
            setOpenModal={
              setOpenModalCheck
            }
            setModalAction={
              setModalAction
            }
          />
        }
      />

      <ModalForAddOrgOrSite
        auth="Organization"
        createStandalone
        display={openModalForAddOrg}
        title={modalForAddOrgTitle}
        setOpenModal={setOpenModalForAddOrg}
        cancelBtn={
          modalForAddOrgCancelBtn
        }
        saveBtn={modalForAddOrgSaveBtn}
        errorHandling={
          handleActionDenied
        }
        refresh={getDatas}
      />

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default Realm;
